import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { formatTL } from '../../utils/money';
import './FleetPaymentBreakdown.css';

// Dönem modeli KAYIT ANINDA hakedişe yazılır (FleetPayment.PricingModel: 0 PlatformResells /
// 1 PassThrough). Kesinti tutarından türetmek yanlıştı: kesintisi 0 tanımlanmış bir aracılık
// filosunda dönem "Yeniden Satış" etiketleniyor ve "−0 TL" hayalet satırı çıkıyordu.
// Alan gelmeyen ESKİ kayıtlarda (null/undefined) eski sezgiye düşülür.
export const resolvePricingModel = (payment) => {
    if (payment?.pricingModel == null) return (payment?.totalPlatformFee ?? 0) > 0 ? 1 : 0;
    return Number(payment.pricingModel);
};

// Kuruş hassasiyetinde sıfır mı? Kayan noktalı toplamlarda 0.0000001 gibi artıklar kalır.
const isZeroKurus = (value) => Math.round(Number(value || 0) * 100) === 0;

// CSV: TR Excel nokta ondalıklı sayıyı METİN okur. Tutarlar 2 hane + virgül ondalık yazılır.
const csvAmount = (value) => Number(value ?? 0).toFixed(2).replace('.', ',');
// Ayraç ';' olduğu için metin alanlarındaki ';' kolonları kaydırır — boşluğa çevrilir.
const csvText = (value) => String(value ?? '').replace(/;/g, ' ');

/**
 * Hakediş kırılımı — iki modelde farklı kalemler gösterir.
 * Aracılık: brüt − kesinti − kesinti KDV'si − zaman-bazlı gider = faturalanacak
 * Yeniden satış: brüt − platformun ödediği kurye ücreti − zaman-bazlı gider = faturalanacak
 *
 * Kalemler toplamı "Faturalanacak" ile tutmuyorsa fark AÇIK bir satır olarak yazılır
 * (dönem içinde bölgelere göre farklı model uygulanmış olabilir) — toplam daima tutar.
 *
 * `onLoadLines` verilmezse paket bazlı döküm düğmesi HİÇ gösterilmez (kırılım kartı çalışır):
 * satırları yükleyemeyen bağlamda düğme daima "kayıt yok" diyordu.
 */
const FleetPaymentBreakdown = ({ payment, lines = [], linesLoading = false, linesLoaded = false, onLoadLines }) => {
    const [open, setOpen] = useState(false);

    const isPassThrough = resolvePricingModel(payment) === 1;
    const gross = payment?.totalGrossPackageAmount ?? 0;
    const fee = payment?.totalPlatformFee ?? 0;
    const vat = payment?.totalPlatformFeeVat ?? 0;
    const timeCost = payment?.timeBasedCourierCost ?? 0;
    const net = payment?.totalFleetShareAmount ?? 0;

    // Yeniden satışta platformun ödediği kurye ücreti kalem olarak verilmiyor; brüt ile netin
    // farkından okunur. Brüt yoksa (eski kayıt) satır hiç gösterilmez.
    const courierPaid = gross > 0 ? Math.max(gross - net - timeCost, 0) : 0;

    // Eksi kalemler. Değeri 0 olanlar HİÇ render edilmez ("−0 TL" hayalet satırı).
    const negativeRows = useMemo(() => {
        const rows = isPassThrough
            ? [
                { key: 'fee', label: 'Platform kesintisi', value: fee },
                { key: 'vat', label: "Kesinti KDV'si", value: vat },
            ]
            : [
                { key: 'courierPaid', label: 'Platformun ödediği kurye ücreti', value: courierPaid },
            ];
        rows.push({ key: 'timeCost', label: 'Zaman-bazlı kurye gideri', value: timeCost });
        return rows.filter(r => !isZeroKurus(r.value));
    }, [isPassThrough, fee, vat, courierPaid, timeCost]);

    // Kalemler toplamı ile faturalanacak tutar arasındaki fark. Brütü olmayan ESKİ kayıtlarda
    // kırılım zaten çizilmediği için hesaplanmaz (yoksa net kadar sahte bir fark satırı çıkardı).
    const residual = gross > 0
        ? net - (gross - negativeRows.reduce((sum, r) => sum + Number(r.value || 0), 0))
        : 0;
    const showResidual = gross > 0 && !isZeroKurus(residual);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        // linesLoaded: "hiç yüklenmedi" ile "yüklendi ama 0 kayıt döndü" ayrımı için — yalnızca
        // lines.length===0'a bakılırsa gerçekten boş dönen hakedişlerde her açılışta yeniden istek atılır.
        if (next && !linesLoaded) onLoadLines?.();
    };

    const csv = useMemo(() => {
        const head = 'Sipariş No;Tarih;Kurye;Brüt;Kesinti;KDV;Filo Payı';
        const rows = lines.map(l => [
            csvText(l.orderNo),
            csvText(new Date(l.calculatedAt).toLocaleString('tr-TR')),
            csvText(l.courierName),
            csvAmount(l.grossPackageAmount), csvAmount(l.platformFee),
            csvAmount(l.platformFeeVat), csvAmount(l.fleetShare)
        ].join(';'));
        return [head, ...rows].join('\n');
    }, [lines]);

    const downloadCsv = () => {
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hakedis-${payment?.period || payment?.id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fpb">
            <div className="fpb-head">
                <span className="fpb-model">{isPassThrough ? 'Aracılık' : 'Yeniden Satış'}</span>
                <span className="fpb-count">{payment?.totalDeliveries ?? 0} paket</span>
            </div>

            <div className="fpb-rows">
                {gross > 0 && (
                    <div className="fpb-row">
                        <span>Brüt paket bedeli</span>
                        <strong>{formatTL(gross)}</strong>
                    </div>
                )}
                {negativeRows.map(row => (
                    <div className="fpb-row neg" key={row.key}>
                        <span>{row.label}</span>
                        <strong>−{formatTL(row.value)}</strong>
                    </div>
                ))}
                {showResidual && (
                    <div
                        className={`fpb-row${residual < 0 ? ' neg' : ''}`}
                        title="Kalemler toplamı ile faturalanacak tutar arasındaki fark. Dönem içinde bölgelere göre farklı fiyatlandırma modeli uygulandıysa burada görünür."
                    >
                        <span>Diğer kalemler</span>
                        <strong>{residual < 0 ? '−' : '+'}{formatTL(Math.abs(residual))}</strong>
                    </div>
                )}
                <div className="fpb-row total">
                    <span>Faturalanacak (KDV dahil)</span>
                    <strong>{formatTL(net)}</strong>
                </div>
            </div>

            {onLoadLines && (
                <button type="button" className="fpb-toggle" onClick={toggle}>
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Paket bazlı döküm
                </button>
            )}

            {open && onLoadLines && (
                <div className="fpb-lines">
                    {linesLoading ? (
                        <div className="fpb-empty">Yükleniyor...</div>
                    ) : lines.length === 0 ? (
                        <div className="fpb-empty">Bu hakedişe bağlı paket kaydı yok.</div>
                    ) : (
                        <>
                            <div className="fpb-lines-actions">
                                <button type="button" className="fpb-csv" onClick={downloadCsv}>
                                    <Download size={13} /> CSV indir
                                </button>
                            </div>
                            <div className="fpb-table-wrap">
                                <table className="fpb-table">
                                    <thead>
                                        <tr>
                                            <th>Sipariş No</th><th>Tarih</th><th>Kurye</th>
                                            <th>Brüt</th><th>Kesinti</th><th>KDV</th><th>Filo Payı</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map(l => (
                                            <tr key={l.deliveryId}>
                                                <td>{l.orderNo || '-'}</td>
                                                <td>{new Date(l.calculatedAt).toLocaleString('tr-TR')}</td>
                                                <td>{l.courierName || '-'}</td>
                                                <td>{formatTL(l.grossPackageAmount)}</td>
                                                <td>{l.platformFee > 0 ? `−${formatTL(l.platformFee)}` : '-'}</td>
                                                <td>{l.platformFeeVat > 0 ? `−${formatTL(l.platformFeeVat)}` : '-'}</td>
                                                <td>{formatTL(l.fleetShare)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FleetPaymentBreakdown;
